// SMS History strip for the Call Detail page. Reads from
// `sms_dispatches` via /api/analytics/calls/{call_id}/sms-history and
// renders every SMS the tenant has already sent to this caller so the
// rep sees what was tried before making another call. Includes both
// this-call dispatches and any prior dispatches to the same phone
// within the tenant.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageSquare, Loader2, CheckCircle2, XCircle, ExternalLink, Beaker, Reply } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
};

const SmsHistoryStrip = ({ callId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!callId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/analytics/calls/${encodeURIComponent(callId)}/sms-history`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ count: 0, dispatches: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [callId]);

  if (loading) return null;
  if (!data || (data.count === 0 && !(data.inbound || []).length)) return null;

  // Merge outbound dispatches + inbound replies into one thread,
  // newest first, so reps read the actual two-way conversation.
  const thread = [
    ...(data.dispatches || []).map((d) => ({ ...d, _dir: 'out', _ts: d.sent_at })),
    ...(data.inbound || []).map((m) => ({ ...m, _dir: 'in', _ts: m.received_at })),
  ].sort((a, b) => String(b._ts || '').localeCompare(String(a._ts || '')));

  return (
    <div
      className="rounded-lg border border-purple-200 bg-purple-50 p-3"
      data-testid="sms-history-strip"
    >
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare className="w-3.5 h-3.5 text-purple-700" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-purple-800">
          SMS History for this caller
        </p>
        <span className="text-[10px] text-purple-600" data-testid="sms-history-count">
          {data.count} sent{(data.inbound || []).length > 0 && ` · ${data.inbound.length} repl${data.inbound.length === 1 ? 'y' : 'ies'}`}
        </span>
      </div>
      <ul className="space-y-1.5">
        {thread.map((d) => {
          if (d._dir === 'in') {
            return (
              <li
                key={d.id}
                className="text-xs rounded border bg-emerald-50 border-emerald-200 p-2"
                data-testid={`sms-inbound-row-${d.id}`}
              >
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900 inline-flex items-center gap-0.5">
                    <Reply className="w-2.5 h-2.5" /> Reply from caller
                  </span>
                  <span className="text-slate-500 tabular-nums">{_fmt(d.received_at)}</span>
                </div>
                <p className="mt-1 text-xs text-emerald-900 whitespace-pre-wrap">{d.content}</p>
              </li>
            );
          }
          const success = d.via === 'yabbr' && d.yabbr_id;
          const gsOk = d.gymsales_note?.ok;
          const isExpanded = expandedId === d.id;
          return (
            <li
              key={d.id}
              className={`text-xs rounded border bg-white/70 border-purple-100 p-2 ${d.yabbr_simulated ? 'ring-1 ring-amber-300' : ''}`}
              data-testid={`sms-history-row-${d.id}`}
            >
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-200 text-purple-800">
                  {d.via === 'yabbr' ? 'Yabbit SMS' : 'GymSales link'}
                </span>
                {d.yabbr_simulated && (
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 inline-flex items-center gap-0.5"
                    title="Simulated send (test-fire) — no SMS actually delivered"
                  >
                    <Beaker className="w-2.5 h-2.5" /> simulated
                  </span>
                )}
                <span className="text-slate-500 tabular-nums">{_fmt(d.sent_at)}</span>
                {d.sent_by_agent_id && (
                  <span className="text-slate-500">
                    by <span className="font-mono">{d.sent_by_agent_id}</span>
                  </span>
                )}
                {success ? (
                  <span className="text-emerald-700 inline-flex items-center gap-0.5">
                    <CheckCircle2 className="w-3 h-3" /> {d.delivery_status || 'delivered'}
                  </span>
                ) : d.via === 'gymsales_link' ? (
                  <span className="text-slate-500 inline-flex items-center gap-0.5">
                    <ExternalLink className="w-3 h-3" /> opened in GymSales
                  </span>
                ) : (
                  <span className="text-rose-700 inline-flex items-center gap-0.5">
                    <XCircle className="w-3 h-3" /> unknown
                  </span>
                )}
                {gsOk && (
                  <span className="text-[10px] text-emerald-700" title="Note posted to GymSales">
                    · CRM note ✓
                  </span>
                )}
                <select
                  value={d.outcome || ''}
                  onChange={async (e) => {
                    const v = e.target.value || null;
                    try {
                      await axios.patch(`${API}/analytics/sms-dispatches/${d.id}/outcome`, { outcome: v });
                      setData((prev) => ({
                        ...prev,
                        dispatches: prev.dispatches.map((x) => (x.id === d.id ? { ...x, outcome: v } : x)),
                      }));
                    } catch { /* keep prior value */ }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className={`text-[10px] border rounded px-1 py-0.5 ${d.outcome ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-500'}`}
                  title="Tag what happened after this SMS — feeds conversion reporting"
                  data-testid={`sms-outcome-${d.id}`}
                >
                  <option value="">outcome…</option>
                  <option value="won">won</option>
                  <option value="follow_up_booked">follow-up booked</option>
                  <option value="no_response">no response</option>
                  <option value="not_interested">not interested</option>
                </select>
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : d.id)}
                  className="ml-auto text-[10px] text-purple-700 hover:text-purple-900"
                >
                  {isExpanded ? 'Hide' : 'Show'} content
                </button>
              </div>
              {isExpanded && (
                <p className="mt-1.5 text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">
                  {d.content}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SmsHistoryStrip;
